import React, { useState, useEffect } from 'react';
import ScoreBreakdown from './ScoreBreakdown';
import DriverLicense from "./LicenseDetail";
import { APP_BASE_URL } from '../config/api';
import { RiskAssessmentCard } from './ui';

const MvrScorer = ({ data, account_id, sub_entity_id, driver_id, onScoreUpdate, onToggleFilters }) => {
  const [loading, setLoading] = useState(false);
  const [scoreResult, setScoreResult] = useState(null);
  const [error, setError] = useState(null);
  const [analysisExists, setAnalysisExists] = useState(false);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    let mounted = true;
    // Function to check if an analysis already exists for the given driver, entity, and sub-entity IDs
    // This function will be called when the component mounts or when the IDs change
    const checkExistingAnalysis = async () => {
      // Check if all required IDs are available before making the API call
      if (!driver_id || !account_id || !sub_entity_id) return;

      try {
        const response = await fetch(
          `${APP_BASE_URL}/api/check_analysis?driver_id=${driver_id}&account_id=${account_id}&sub_entity_id=${sub_entity_id}`
        );
        if (!mounted) return;
        
        if (response.ok) {
          const data = await response.json();
          setAnalysisExists(data.exists);
          if (data.exists && data.score) {
            setScoreResult(data.score);
            onScoreUpdate?.(data.score);
          }
        }
      } catch (err) {
        console.error("Failed to check existing analysis:", err);
      }
    };

    checkExistingAnalysis();

    return () => {
      mounted = false;
    };
  }, [driver_id, account_id, sub_entity_id]); // Only dependencies that should trigger a re-fetch

  // Helper function to check for valid records
  const hasValidRecords = (data) => {
    if (!data?.driving_record?.["xml-history"]) return false;
    
    const drivingHistory = data.driving_record["xml-history"];
    // Check if there are any records in any category
    const hasRecords = Object.values(drivingHistory).some(category => 
      Array.isArray(category) && category.length > 0
    );
    
    return hasRecords;
  };

  // Function to handle the score button click
  // This function checks if an analysis already exists and if so, shows an error message
  // If no analysis exists, it makes a POST request to score the driver
  // It updates the scoreResult state and calls the onScoreUpdate callback with the new score
  // It also handles loading state and error handling
  const handleScoreClick = async () => {
    if (analysisExists) {
      setError('Analysis already exists. To re-run the analysis, please make a payment.');
      return;
    }
    
    // Add this check
    if (!hasValidRecords(data)) {
      setError('Insufficient driving history to generate a score.');
      return;
    }
    
    // Check if data is available before scoring
    // If no driving record data is available, show an error message
    setLoading(true);
    setError(null);
    try {
      // Check if the driving record data is available
      // If not, show an error message
      const response = await fetch(`${APP_BASE_URL}/api/score_driver?driver_id=${driver_id}&account_id=${account_id}&sub_entity_id=${sub_entity_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ driving_history: queryInfo }),
      });
      // If the response is not OK, throw an error
      // This will be caught by the catch block below
      if (!response.ok) throw new Error('Failed to get score');
      // If the response is OK, parse the JSON data
      const result = await response.json();
      
      setScoreResult(result);
      // Call the callback when new score is generated
      onScoreUpdate?.(result);
    } catch (err) {
      console.error(err);
      setError('Something went wrong while scoring the MVR.');
    } finally {
      setLoading(false);
    }
  };
  // If no driving record data is available, show a message indicating that data is being fetched
  if (!data?.driving_record) {
    return (
      <div className="bg-white border border-neutral-200 rounded-lg text-center p-4 shadow-sm">
        <div className="animate-pulse">
          <div className="h-4 w-32 bg-neutral-200 rounded mx-auto mb-2" />
          <div className="h-3 w-48 bg-neutral-100 rounded mx-auto" />
        </div>
      </div>
    );
  }
  // Extracting the driving record information from the data prop
  const queryInfo = data.driving_record;

  const mvrPullDateStr = queryInfo.order?.report_date;
  let daysAgo = null;
  if (mvrPullDateStr) {
    const mvrPullDate = new Date(mvrPullDateStr);
    const now = new Date();
    const diffTime = now - mvrPullDate;
    daysAgo = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  }

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
      {/* Top Section - Driver License */}
      <div className="mb-3">
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <DriverLicense data={data} />
        </div>
      </div>

      {/* Risk Assessment - Unified Component */}
      <RiskAssessmentCard
        scoreResult={scoreResult}
        hasValidRecords={hasValidRecords(data)}
        loading={loading}
        onGenerateScore={handleScoreClick}
        onToggleFilters={onToggleFilters}
        mvrDaysAgo={daysAgo}
        error={error}
        breakdown={
          scoreResult ? (
            <ScoreBreakdown
              score={scoreResult.score}
              risk_level={scoreResult.risk_level}
              explanation={scoreResult.explanation}
            />
          ) : null
        }
      />
    </div>
  );
};

export default MvrScorer;